import $ from 'jquery'
import { Controller } from 'stimulus'
import { handleDataFocus } from '~/common/behaviors/form_focus'
import {
  registerEscLayer,
  unregisterEscLayer,
} from '~/common/helpers/esc_stack'
import { focusInto, focusLost } from '~/common/helpers/focus_trap'
import './side_panel.css'

// Registered with escape: false, so never called: the panel doesn't close on Escape, it's on the
// Esc stack for its focus trap. Non-modal, so anything else open (a modal from inside it, a
// dropdown) takes over the trap until it closes. One shared key, because controls inside the
// panel (the header's close chevron) are side-panel controllers of their own.
const sidePanelLayer = () => false

export default class extends Controller {
  connect() {
    this.panel = document.getElementById('side_panel')
    this.content = document.getElementById('side_panel_content')
    this.#loadSavedWidth()
    if (this.#isPanel && this.panel.classList.contains('open')) {
      this.#registerLayer()
    }
  }

  disconnect() {
    if (this.#isPanel) unregisterEscLayer('side-panel', sidePanelLayer)
  }

  get #isPanel() {
    return this.element === this.panel
  }

  show(event) {
    const panel = this.panel
    const content = this.content
    const opening = !panel.classList.contains('open')
    this.focusAtShow = document.activeElement
    const takeFocus = opening || this.#mayTakeFocus()
    // Before swapping content, so the opener is recorded while it's still the focused element
    this.#registerLayer()
    $(content).html(event.detail.content)

    this.#loadSavedWidth()

    if (panel.dataset.addedClass) {
      panel.classList.remove(panel.dataset.addedClass)
    }

    if (content.dataset.addedClass) {
      content.classList.remove(content.dataset.addedClass)
    }

    if (!event.detail.preserve_scroll) {
      panel.scrollTop = 0
    }

    if (event.detail.class) {
      panel.classList.add(event.detail.class)
      panel.dataset.addedClass = event.detail.class
    }

    if (event.detail.content_class) {
      content.classList.add(event.detail.content_class)
      content.dataset.addedClass = event.detail.content_class
    }

    if (panel.classList.contains('open')) {
      this.onContentUpdated({ takeFocus })
    } else {
      panel.classList.add('open')
      panel.addEventListener('transitionend', this.onShowTransitionEnd)
    }

    $(panel).changed()
  }

  // Single close path for every way the panel is dismissed:
  // 1) Close chevron / cancel buttons → Stimulus click→hide
  // 2) AJAX save with hide_side_panel:true → ajax_helpers dispatches CustomEvent('hide')
  //    on #side_panel (data-action hide→side-panel#hide) — does NOT remove .open itself.
  hide(e) {
    e?.preventDefault?.()
    // TipTap BubbleMenu toolbars live in tippy (fixed, often under body) and do not always
    // hide on blur when the panel slides closed — force-dismiss so they don't stick over the page.
    this.#hideTiptapBubbleMenus()
    this.panel.classList.remove('open')
    unregisterEscLayer('side-panel', sidePanelLayer)
    document.dispatchEvent(new CustomEvent('side_panel:hide'))

    this.panel.querySelectorAll('video').forEach((video) => {
      video.pause()
    })
  }

  onShowTransitionEnd = (e) => {
    // Don't care about transitions on children
    if (e.currentTarget !== e.target) return

    // Opening takes focus, unless it moved somewhere the panel shouldn't take it from while the
    // panel slid in
    this.onContentUpdated({
      takeFocus:
        document.activeElement === this.focusAtShow || this.#mayTakeFocus(),
    })

    // Cleanup listener
    this.panel.removeEventListener('transitionend', this.onShowTransitionEnd)
  }

  onContentUpdated({ takeFocus = true } = {}) {
    if (!takeFocus) return
    // Focus on the first visible form field, if the form is asking for it
    handleDataFocus(this.panel.querySelector('[data-focus]'))
    // Otherwise the panel itself, so Tab starts inside it
    focusInto(this.panel, { first: false })
  }

  // New content doesn't take focus from someone typing on the page (e.g. a background update). A
  // click on a page link or button that loaded it still hands focus over, like data-focus always did.
  #mayTakeFocus() {
    const active = document.activeElement
    return (
      this.panel.contains(active) ||
      focusLost(active) ||
      !active.matches('input, textarea, select, [contenteditable]')
    )
  }

  #registerLayer() {
    registerEscLayer('side-panel', sidePanelLayer, {
      escape: false,
      focusTrap: this.panel,
    })
  }

  resizeStarted(e) {
    this.width = this.panel.getBoundingClientRect().width
    document.documentElement.classList.add('side-panel-resizing')
  }

  resized(e) {
    let newWidth = this.width - e.detail.dx
    this.panel.style.setProperty('--simplero-side-panel-width', `${newWidth}px`)
  }

  resizeStopped(e) {
    document.documentElement.classList.remove('side-panel-resizing')

    const key = this.#getWidthStorageKey()
    if (!key) return

    const currentWidth = getComputedStyle(this.panel)
      .getPropertyValue('--simplero-side-panel-width')
      .trim()

    localStorage.setItem(key, currentWidth)
  }

  #loadSavedWidth() {
    const key = this.#getWidthStorageKey()

    if (key) {
      const savedWidth = localStorage.getItem(key)
      if (savedWidth) {
        this.panel.style.setProperty('--simplero-side-panel-width', savedWidth)
        return
      }
    }

    this.panel.style.removeProperty('--simplero-side-panel-width')
  }

  // Content can name its own width key (data-side-panel-width-key), so a panel that defaults wider
  // (send-email steps) remembers its width apart from the regular panel's.
  #getWidthStorageKey() {
    const keyed = this.content.querySelector('[data-side-panel-width-key]')
    if (keyed) return `side_panel_width_${keyed.dataset.sidePanelWidthKey}`

    const element = this.content.querySelector('[id]')
    if (!element) return
    return `side_panel_width_${element.id.replace(/\d/g, '')}`
  }

  #hideTiptapBubbleMenus() {
    this.panel
      .querySelectorAll('[data-controller~="tiptap--editor"]')
      .forEach((el) => {
        const controller = this.application.getControllerForElementAndIdentifier(
          el,
          'tiptap--editor'
        )
        controller?.hideBubbleMenu?.()
      })
  }
}
